// ── Client Bank Acct Detail ─────────────────────────────────────────────────
//
// The borrowers' bank activity, sliced the way the Power BI report sliced it:
// by date, by account status, by company, by bank and by last four. This is the
// report that lived outside the platform; it lives here now because the numbers
// behind it do.
//
// MONEY IN IS POSITIVE HERE. Plaid's own convention is the opposite — a deposit
// arrives as a negative amount — and dbo.customerBankTransaction stores it raw,
// because 155,440 migrated rows were written under that convention and flipping
// the stored values would have rewritten five years of history. The flip
// happens once, in dbo.vBankTransactionDetail as `signedamount`, and this page
// reads that column rather than negating anything itself. The old report showed
// receipts as negatives; this one does not, and that is a deliberate break —
// two screens in one product disagreeing about the sign of a receipt is how an
// argument about a number ends badly.
//
// THE DETAIL GRID IS PAGED ON THE SERVER, 500 rows at a time, with sorting and
// search done there over every matching row. The count above the grid is the
// true total, so a page never reads as the whole population.
//
// THE SLICERS CROSS-FILTER, the way they do in the report this replaces. Tick a
// company and the Bank and Last 4 lists narrow to that company's accounts.
//
// Two rules keep that from becoming a trap, and both matter:
//
//   1. A slicer is never filtered by its own selection. The server leaves each
//      dimension out of its own facet query, so the Company list stays whole
//      while you are picking companies and a second one can still be added.
//      Without this a slicer collapses to the single value you ticked and the
//      only way to widen it is to clear everything and start again.
//
//   2. A value that is ticked but has dropped out of its list — because some
//      other slicer excluded it — is STILL AN ACTIVE FILTER, and it stays on
//      screen, dimmed, so it can be switched off. Hiding it would leave the
//      report filtered by something invisible, which looks like missing data
//      and gets reported as a bug about the numbers rather than the slicer.
//      Keeping the label requires remembering it, which is what `labels` is.
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { B, INK_MUTED, INK_PRIMARY, HAIRLINE } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, Btn, Modal, Pagination, SearchBar, SectionHeader, STh, Td, Th } from "../../../shared/ui";
import { fmt, fmtDate, exportCSV, localFilter } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";
import { useChartTheme, ChartThemeToggle, chartCardStyle, useWidth } from "../../../shared/chartTheme";

export const short = (n) => {
  const a = Math.abs(Number(n) || 0), s = n < 0 ? "-" : "";
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${s}$${Math.round(a / 1e3)}K`;
  return `${s}$${a.toFixed(0)}`;
};
const num = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const dayMs = 86400000;
const iso = (d) => d.toISOString().slice(0, 10);

// ── DEFAULTS ──
// The page used to open on every transaction ever (170K+) for every borrower,
// paid off included, and nobody reads that. It now opens on the last 90 days
// of the active book; the range buttons and the Status slicer widen it.
const ACTIVE_STATUSES = ["Accrual", "Non Accrual"];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const RANGES = [
  { key: "30",  label: "30D", from: () => daysAgo(30) },
  { key: "90",  label: "90D", from: () => daysAgo(90) },
  { key: "ytd", label: "YTD", from: () => new Date().getFullYear() + "-01-01" },
  { key: "1y",  label: "1Y",  from: () => daysAgo(365) },
  { key: "all", label: "All", from: () => "" },
];

// ── a checkbox slicer, the shape the report used ────────────────────────────
const MultiPick = ({ label, options, selected, onChange, height = 150 }) => {
  const [q, setQ] = useState("");
  const shown = q
    ? options.filter((o) => String(o.label).toLowerCase().includes(q.toLowerCase()))
    : options;
  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    onChange(next);
  };
  return (
    <Card style={{ padding: 10, minWidth: 190, flex: "1 1 190px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: INK_PRIMARY }}>{label}</span>
        {selected.size > 0 && (
          <a href="#" onClick={(e) => { e.preventDefault(); onChange(new Set()); }}
             style={{ fontSize: 11, color: B.cyan, textDecoration: "none", marginLeft: "auto" }}>
            clear ({selected.size})
          </a>
        )}
      </div>
      {options.length > 8 && (
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…"
               style={{ width: "100%", fontSize: 11, padding: "3px 6px", marginBottom: 6,
                        border: "1px solid " + HAIRLINE, borderRadius: 4 }} />
      )}
      <div style={{ maxHeight: height, overflowY: "auto" }}>
        {shown.length === 0 && (
          <div style={{ fontSize: 11, color: INK_MUTED, padding: "4px 0" }}>Nothing matches.</div>
        )}
        {shown.map((o) => (
          <label key={o.value} style={{ display: "flex", gap: 6, alignItems: "center",
                                        fontSize: 12, padding: "2px 0", cursor: "pointer",
                                        opacity: o.stale ? 0.55 : 1 }}
                 title={o.stale
                   ? `${o.label} — still filtering, but nothing matches it under the other slicers. Untick to drop it.`
                   : o.label}>
            <input type="checkbox" checked={selected.has(o.value)}
                   onChange={() => toggle(o.value)} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis",
                           whiteSpace: "nowrap" }}>{o.label}</span>
            {o.stale && <span style={{ fontSize: 10, color: "#8A6100" }}>·0</span>}
          </label>
        ))}
      </div>
    </Card>
  );
};

// ── net cash by day ─────────────────────────────────────────────────────────
//
// Time-proportional on x, not one slot per row. The series has gaps — weekends,
// quiet months, an account that stopped reporting — and an index-based axis
// would close them up and draw a shape that never happened.
// onSelect(from, to): drag across the chart to pick a date range, or click a
// single day. The page turns that into its From / To filter, so the totals,
// the detail grid and the chart itself all narrow to the selection.
export const NetByDay = ({ series, onSelect }) => {
  // Drawn at the card's real width (see shared/chartTheme useWidth), so the
  // axis text stays its set size however wide the window is.
  const T = useChartTheme();
  const [wrap, W] = useWidth(960);
  const H = 230, PADL = 58, PADR = 18, PADT = 14, PADB = 28;
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null);   // { a, b } in viewBox x units
  if (!series.length) {
    return <div ref={wrap} style={{ padding: 40, textAlign: "center", fontSize: 13, color: T.muted }}>
      Nothing in this range.
    </div>;
  }
  const t0 = new Date(series[0].txnDate).getTime();
  const t1 = new Date(series[series.length - 1].txnDate).getTime();
  const span = Math.max(t1 - t0, dayMs);
  const mag = Math.max(1, ...series.map((p) => Math.abs(Number(p.net) || 0)));
  const x = (d) => PADL + ((new Date(d).getTime() - t0) / span) * (W - PADL - PADR);
  const midY = PADT + (H - PADT - PADB) / 2;
  const y = (v) => midY - ((Number(v) || 0) / mag) * ((H - PADT - PADB) / 2);

  // A tick on 1 January of each year the series covers, which is how the
  // report this replaces labelled it.
  const years = [];
  for (let yr = new Date(t0).getUTCFullYear(); yr <= new Date(t1).getUTCFullYear(); yr++) {
    const t = Date.UTC(yr, 0, 1);
    if (t >= t0 && t <= t1) years.push({ yr, t });
  }
  // Under ~13 months there may be no 1 January in view at all, which left a
  // zoomed chart with no dates on it. Month ticks instead.
  const months = [];
  if (span < 400 * dayMs) {
    const d = new Date(t0);
    for (let m = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1); m <= t1;
         m = Date.UTC(new Date(m).getUTCFullYear(), new Date(m).getUTCMonth() + 1, 1)) {
      months.push({ t: m, label: new Date(m).toLocaleString("en-US", { month: "short", timeZone: "UTC" })
        + (new Date(m).getUTCMonth() === 0 ? " " + new Date(m).getUTCFullYear() : "") });
    }
  }
  const ticks = months.length ? months.map((m) => ({ key: m.t, t: m.t, label: m.label }))
                              : years.map((v) => ({ key: v.yr, t: v.t, label: v.yr }));

  // ── drag to select ──
  const plotL = PADL, plotR = W - PADR;
  const toVx = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return Math.min(plotR, Math.max(plotL, ((e.clientX - r.left) / r.width) * W));
  };
  const toIso = (vx) => new Date(t0 + ((vx - plotL) / (plotR - plotL)) * span).toISOString().slice(0, 10);
  const down = (e) => {
    if (!onSelect || e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const v = toVx(e); setDrag({ a: v, b: v });
  };
  const moveP = (e) => { if (drag) setDrag({ ...drag, b: toVx(e) }); };
  const up = () => {
    if (!drag) return;
    const lo = Math.min(drag.a, drag.b), hi = Math.max(drag.a, drag.b);
    setDrag(null);
    if (hi - lo < 4) {
      // A click: the nearest day that has activity.
      const day = toIso(lo);
      const near = series.reduce((best, p) =>
        Math.abs(new Date(p.txnDate) - new Date(day)) < Math.abs(new Date(best.txnDate) - new Date(day)) ? p : best, series[0]);
      const d = String(near.txnDate).slice(0, 10);
      onSelect(d, d);
    } else {
      onSelect(toIso(lo), toIso(hi));
    }
  };

  return (
    <div ref={wrap}>
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width={W} height={H}
         style={{ display: "block", overflow: "visible", cursor: onSelect ? "crosshair" : "default",
                  userSelect: "none", touchAction: "none" }}
         onPointerDown={down} onPointerMove={moveP} onPointerUp={up}
         onPointerCancel={() => setDrag(null)}>
      <defs>
        <filter id="nbd-glow" x="-5%" y="-30%" width="110%" height="160%">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {[1, 0.5, -0.5, -1].map((g) => (
        <g key={g}>
          <line x1={PADL} x2={W - PADR} y1={y(g * mag)} y2={y(g * mag)}
                stroke={T.grid} strokeDasharray="2 3" />
          <text x={PADL - 8} y={y(g * mag) + 4} textAnchor="end"
                style={{ fontSize: 10, fill: T.muted }}>{short(g * mag)}</text>
        </g>
      ))}
      <line x1={PADL} x2={W - PADR} y1={midY} y2={midY} stroke={T.gridFirm} />
      <text x={PADL - 8} y={midY + 4} textAnchor="end"
            style={{ fontSize: 10, fill: T.muted }}>$0</text>

      {ticks.map((v) => (
        <g key={v.key}>
          <line x1={x(v.t)} x2={x(v.t)} y1={PADT} y2={H - PADB} stroke={T.grid} />
          <text x={x(v.t)} y={H - PADB + 14} textAnchor="middle"
                style={{ fontSize: 10, fill: T.muted }}>{v.label}</text>
        </g>
      ))}

      <polyline fill="none" stroke={T.accent} strokeWidth={T.dark ? 1.4 : 1}
        strokeLinejoin="round" filter={T.dark ? "url(#nbd-glow)" : undefined}
        points={series.map((p) => `${x(p.txnDate)},${y(p.net)}`).join(" ")} />

      {/* Hover targets only on the biggest days — 1,800 <title> elements is a
          lot of DOM for information nobody reads on a quiet Tuesday. */}
      {[...series]
        .sort((a, b) => Math.abs(b.net) - Math.abs(a.net))
        .slice(0, 60)
        .map((p) => (
          <circle key={p.txnDate} cx={x(p.txnDate)} cy={y(p.net)} r="2.5"
                  fill={Number(p.net) >= 0 ? T.accentSoft : T.warm}>
            <title>{`${fmtDate(p.txnDate)}\nNet ${fmt(p.net)}\n`
              + `In ${fmt(p.cash_in)} · Out ${fmt(p.cash_out)}\n${p.txns} transactions`}</title>
          </circle>
        ))}

      {drag && Math.abs(drag.b - drag.a) >= 1 && (
        <g pointerEvents="none">
          <rect x={Math.min(drag.a, drag.b)} y={PADT} width={Math.abs(drag.b - drag.a)} height={H - PADT - PADB}
                fill={T.accentSoft} fillOpacity="0.15" stroke={T.accentSoft} strokeWidth="1" />
          <text x={(drag.a + drag.b) / 2} y={PADT + 12} textAnchor="middle"
                style={{ fontSize: 10, fill: T.accent, fontWeight: 600 }}>
            {fmtDate(toIso(Math.min(drag.a, drag.b)))} – {fmtDate(toIso(Math.max(drag.a, drag.b)))}
          </text>
        </g>
      )}
    </svg>
    </div>
  );
};

const Stat = ({ label, value, note }) => (
  <Card style={{ padding: "12px 14px", flex: "1 1 150px" }}>
    <div style={{ fontSize: 11, color: INK_MUTED, textTransform: "uppercase",
                  letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 600, color: INK_PRIMARY,
                  fontVariantNumeric: "tabular-nums" }}>{value}</div>
    {note && <div style={{ fontSize: 11, color: INK_MUTED, marginTop: 3 }}>{note}</div>}
  </Card>
);

// Pending is money the bank has told us about but not settled. It is worth a
// chip rather than a word: a row that still might change amount, or vanish, is
// not the same kind of fact as a posted one, and on a page full of figures the
// difference has to survive being skimmed.
const TxnState = ({ pending }) => (
  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 999, fontWeight: 600,
                 background: pending ? "#FBF0D9" : "#E1F5EE",
                 color:      pending ? "#8A6100" : "#0F6E56" }}>
    {pending ? "Pending" : "Posted"}
  </span>
);

const Money = ({ v }) => (
  <span style={{ fontVariantNumeric: "tabular-nums",
                 color: Number(v) < 0 ? "#993C1D" : INK_PRIMARY }}>
    {v === null || v === undefined ? "—" : fmt(v)}
  </span>
);

export const ClientBankDetailPage = () => {
  const { api, role } = useApp();
  const chartT = useChartTheme();
  // Administrators: archive and remove the imported likely duplicates in the
  // current Company filter and date range (api/removeBankDuplicates).
  const [dupFix, setDupFix] = useState(null);   // null | { loading, data, err, busy, done }

  const today = useMemo(() => iso(new Date()), []);
  const [from, setFrom]   = useState(() => daysAgo(90));
  const [to, setTo]       = useState(today);
  const [tab, setTab]     = useState("transactions");
  const [companies, setCompanies] = useState(new Set());
  const [banks, setBanks] = useState(new Set());
  const [last4, setLast4] = useState(new Set());
  const [statuses, setStatuses] = useState(() => new Set(ACTIVE_STATUSES));
  // The TRANSACTION's state — Pending or Posted — which is a different thing
  // from the borrower's account status above. They used to share one column on
  // this page and it read as one fact; they are two.
  const [txnStatus, setTxnStatus] = useState(new Set());
  // Likely duplicates (see api/getClientBankDetail): hidden by default so the
  // totals and chart count each transaction once; "show" flags them in the
  // grid, "only" lists just them for review.
  const [dups, setDups] = useState("hide");
  const [q, setQ]         = useState("");

  // ── THE TRANSACTION GRID IS PAGED ON THE SERVER ──
  // 500 rows a page — the most recent 500 by default. Sorting and the search box are applied by the API over
  // every matching transaction, so a sort by Amount finds the real largest one.
  // A page turn, a sort or a search refetches only the grid (part=rows) — the
  // chart, totals and slicers stay as they are.
  const GRID_SIZE = 500;
  const [gridPage, setGridPage] = useState(1);
  const [sortKey, setSortKey]   = useState("");       // "" = newest first
  const [sortDir, setSortDir]   = useState("desc");
  const [qServer, setQServer]   = useState("");       // the search box, debounced
  const [grid, setGrid]         = useState({ rows: [], total: 0 });
  const [gridLoading, setGridLoading] = useState(false);
  const gridSeq = useRef(0);
  const lastGridKey = useRef("");
  const gridKey = (pg, sk, sd, qs) => [pg, sk, sd, qs].join("|");
  // The full load reads these through a ref so a sort or search does not
  // re-run the chart and totals.
  const gridState = useRef({});
  gridState.current = { sortKey, sortDir, qServer };
  const gridParams = (pg, gs) => {
    const p = { page: pg, pagesize: GRID_SIZE };
    if (gs.sortKey) { p.sort = gs.sortKey; p.dir = gs.sortDir; }
    if (gs.qServer) p.q = gs.qServer;
    return p;
  };

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  const [err, setErr]     = useState(null);
  const [refreshed, setRefreshed] = useState(null);
  // Ranges you zoomed out of by dragging on the chart, newest last, so Back
  // returns to exactly where you were.
  const [zoomStack, setZoomStack] = useState([]);
  const zoomTo = (f, t) => { setZoomStack((z) => [...z, { from, to }]); setFrom(f); setTo(t); };
  const zoomBack = () => {
    if (!zoomStack.length) return;
    const prev = zoomStack[zoomStack.length - 1];
    setFrom(prev.from); setTo(prev.to);
    setZoomStack(zoomStack.slice(0, -1));
  };
  // Balances are fetched only while that tab is open — the query walks every
  // account's balance history and most visits never look at it.
  const [bal, setBal] = useState(null);
  const [balLoading, setBalLoading] = useState(false);
  const balSeq = useRef(0);

  // Every label this page has ever seen, keyed by the value the filter sends.
  // It only ever grows. Once the slicers cross-filter, a ticked company can
  // vanish from the Company facet — a bank selection excluded it — and without
  // a remembered label the page could only render its raw accountId, or worse,
  // drop the row and leave a filter running that nobody can switch off.
  const [labels, setLabels] = useState(() => ({}));

  const join = (s) => (s.size ? [...s].join("|") : undefined);

  // ── WHY THIS COUNTS ITS OWN REQUESTS ────────────────────────────────────
  //
  // A date input does not fire one change. Typing into it fires on every
  // segment, and an incomplete date comes through as "" — so entering a From
  // date sends several requests, some of them UNFILTERED, and each one takes a
  // couple of seconds against 162,920 rows. Whichever reply lands last wins,
  // and it is routinely the early unfiltered one. The page then sits there
  // showing everything, with the right date in the box: "the From filter isn't
  // filtering". It was filtering. Its answer just got overwritten.
  //
  // Every request takes a ticket; a reply that is not the current ticket is
  // dropped. The debounce below cuts the number of requests; this is what makes
  // the result correct, and the two are not substitutes for each other — a
  // debounce alone still races whenever one reply is slower than the next
  // keystroke gap.
  const seq = useRef(0);

  const params = useCallback(() => {
    const p = {};
    if (from) p.from = from;
    if (to)   p.to   = to;
    if (companies.size) p.companies = join(companies);
    if (banks.size)     p.banks     = join(banks);
    if (last4.size)     p.last4     = join(last4);
    if (statuses.size)  p.status    = join(statuses);
    if (txnStatus.size) p.txnstatus = join(txnStatus);
    if (dups !== "hide") p.dups = dups;
    return p;
  }, [from, to, companies, banks, last4, statuses, txnStatus, dups]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Remove likely duplicates (administrators) ──
  // Preview with the page's Company filter and dates, then archive + remove.
  const dupScope = () => {
    const p = params();
    return { companies: p.companies, from: p.from, to: p.to };
  };
  const openDupFix = async () => {
    setDupFix({ loading: true });
    try { setDupFix({ data: await api.post("removeBankDuplicates", { ...dupScope(), commit: false }) }); }
    catch (e) { setDupFix({ err: e.message }); }
  };
  const commitDupFix = async () => {
    setDupFix((d) => ({ ...d, busy: true, err: null }));
    try {
      const r = await api.post("removeBankDuplicates", { ...dupScope(), commit: true });
      setDupFix((d) => ({ ...d, busy: false, done: r }));
      load();
    } catch (e) { setDupFix((d) => ({ ...d, busy: false, err: e.message })); }
  };

  const load = useCallback(() => {
    const ticket = ++seq.current;
    setLoad(true); setErr(null);
    // New filters start the grid again at page 1, in the current sort.
    const gs = gridState.current;
    setGridPage(1);
    lastGridKey.current = gridKey(1, gs.sortKey, gs.sortDir, gs.qServer);
    api.get("getClientBankDetail", { ...params(), balances: 0, ...gridParams(1, gs) })
      .then((d) => {
        if (ticket !== seq.current) return;   // a newer request is in flight
        setData(d);
        setGrid({ rows: (d && d.rows) || [], total: (d && d.gridtotal) || 0 });
        const fa = (d && d.facets) || {};
        setLabels((prev) => {
          const next = { ...prev };
          (fa.companies || []).forEach((c) => { next[c.accountId] = c.companyname; });
          (fa.banks || []).forEach((b) => { next[b] = b; });
          (fa.last4 || []).forEach((l) => { next[l] = l; });
          (fa.statuses || []).forEach((s) => { next[s] = s; });
          (fa.txnstatus || []).forEach((s) => { next[s] = s; });
          return next;
        });
        setRefreshed(new Date()); setLoad(false);
      })
      .catch((e) => {
        if (ticket !== seq.current) return;
        setErr(e.message); setLoad(false);
      });
  }, [api, params]);

  const loadRows = useCallback((pg) => {
    const ticket = ++gridSeq.current;
    const gs = gridState.current;
    lastGridKey.current = gridKey(pg, gs.sortKey, gs.sortDir, gs.qServer);
    setGridLoading(true);
    api.get("getClientBankDetail", { ...params(), part: "rows", ...gridParams(pg, gs) })
      .then((d) => { if (ticket === gridSeq.current) { setGrid({ rows: (d && d.rows) || [], total: (d && d.gridtotal) || 0 }); setGridLoading(false); } })
      .catch((e) => { if (ticket === gridSeq.current) { setErr(e.message); setGridLoading(false); } });
  }, [api, params]); // eslint-disable-line react-hooks/exhaustive-deps

  // The search box is typed into; ask the server once typing pauses.
  useEffect(() => {
    const t = setTimeout(() => { if (q !== qServer) { setQServer(q); setGridPage(1); } }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Page, sort or search changed: fetch that page — unless the full load just
  // fetched exactly it.
  useEffect(() => {
    if (!data) return;
    if (gridKey(gridPage, sortKey, sortDir, qServer) === lastGridKey.current) return;
    loadRows(gridPage);
  }, [gridPage, sortKey, sortDir, qServer]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
    setGridPage(1);
  };

  // Export takes every matching row in the current sort, not just this page.
  const exportTxns = () => {
    api.get("getClientBankDetail", { ...params(), part: "rows", ...gridParams(1, gridState.current), pagesize: 20000 })
      .then((d) => exportCSV(((d && d.rows) || []).map((r) => ({ ...r, txnstate: r.pending ? "Pending" : "Posted" })),
            ["companyname", "bankname", "last4", "txnDate", "signedamount", "name",
             "txnstate", "accountstatus", "feed", "isdup", "dupofid"],
            "client_bank_transactions.csv"))
      .catch((e) => setErr(e.message));
  };

  const loadBalances = useCallback(() => {
    const ticket = ++balSeq.current;
    setBalLoading(true);
    api.get("getClientBankDetail", { ...params(), part: "balances" })
      .then((d) => { if (ticket === balSeq.current) { setBal((d && d.balances) || []); setBalLoading(false); } })
      .catch((e) => { if (ticket === balSeq.current) { setErr(e.message); setBalLoading(false); } });
  }, [api, params]);

  // Balances follow the same filters, but only while their tab is showing.
  // Leaving the tab marks them stale so coming back refetches.
  useEffect(() => {
    if (tab !== "balances") { setBal(null); return; }
    const t = setTimeout(loadBalances, 300);
    return () => clearTimeout(t);
  }, [tab, loadBalances]);

  // Debounced, so half-typed dates and a run of checkbox clicks cost one query
  // rather than six. The cleanup cancels the pending call, so only the last
  // state of the filters is ever asked about.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const f = (data && data.facets)
    || { companies: [], banks: [], last4: [], statuses: [], txnstatus: [] };
  const totals = (data && data.totals) || {};
  const rows = grid.rows;
  const balances = bal || [];
  const activeRange = (RANGES.find((r) => r.from() === from && to === today) || {}).key || null;

  // The facet list, plus any still-ticked value the cross-filter dropped.
  // Appended rather than merged in place so the survivors keep the server's
  // ordering and the orphans collect, visibly, at the bottom.
  const opts = (list, selected) => {
    const present = new Set(list.map((o) => o.value));
    const extra = [...selected]
      .filter((v) => !present.has(v))
      .map((v) => ({ value: v, label: labels[v] || String(v), stale: true }));
    return list.concat(extra);
  };

  // txnstate is carried as a word alongside the bit, so the export writes
  // "Pending"/"Posted" and the search box matches on them too. A column of 1s
  // and 0s headed "pending" is the kind of thing that gets read backwards in a
  // spreadsheet three weeks later.
  const shownRows = rows;   // already searched, sorted and paged by the server
  const shownBal = localFilter(balances, q, ["companyname", "bankname", "last4"]);

  const gridPages = Math.max(1, Math.ceil(grid.total / GRID_SIZE));
  const H = ({ label, k, numeric }) =>
    <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} numeric={numeric} />;

  if (loading && !data) return <Spinner />;
  if (err && !data) return <ErrorMsg msg={err} onRetry={load} />;

  return <>
    <SectionHeader title="Client Bank Acct Detail" actions={[
      <span key="t" style={{ fontSize: 12, color: INK_MUTED, alignSelf: "center" }}>
        {refreshed ? `Refreshed ${refreshed.toLocaleString()}` : ""}
      </span>,
      <Btn key="x" onClick={() => tab === "transactions"
        ? exportTxns()
        : exportCSV(shownBal,
            ["companyname", "bankname", "last4", "asofDate", "currentBalance", "availableBalance"],
            "client_bank_balances.csv")}>Export</Btn>,
      <Btn key="r" onClick={() => { load(); if (tab === "balances") loadBalances(); }}>{loading || balLoading ? "Loading…" : "Reload"}</Btn>,
    ]} />

    <Card style={{ padding: 12, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 3 }}>From</div>
          <input type="date" value={from} max={to || undefined}
                 onChange={(e) => setFrom(e.target.value)}
                 style={{ fontSize: 12, padding: "4px 6px",
                          border: "1px solid " + HAIRLINE, borderRadius: 4 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 3 }}>To</div>
          <input type="date" value={to} min={from || undefined}
                 onChange={(e) => setTo(e.target.value)}
                 style={{ fontSize: 12, padding: "4px 6px",
                          border: "1px solid " + HAIRLINE, borderRadius: 4 }} />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {RANGES.map((r) => (
            <Btn key={r.key} variant={activeRange === r.key ? "primary" : undefined}
                 title={r.key === "all" ? "Every transaction on file — slower" : undefined}
                 onClick={() => { setFrom(r.from()); setTo(today); setZoomStack([]); }}>{r.label}</Btn>
          ))}
        </div>
        {(() => {
          const dn = Number((data && data.dups && data.dups.dup_txns) || 0);
          if (!dn && dups === "hide") return null;   // nothing to say
          return (
            <div title="Same borrower account, same date, same amount, from another feed or with an overlapping description. Nothing is deleted — this only decides what the page counts.">
              <div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 3 }}>Likely duplicates</div>
              <select value={dups} onChange={(e) => setDups(e.target.value)}
                      style={{ fontSize: 12, padding: "4px 6px", border: "1px solid " + HAIRLINE, borderRadius: 4,
                               background: dups === "hide" ? "#FDF0D5" : undefined, color: dups === "hide" ? "#7A5200" : undefined }}>
                <option value="hide">Hidden ({num(dn)} · {fmt(data && data.dups ? data.dups.dup_amount || 0 : 0)})</option>
                <option value="show">Shown and flagged</option>
                <option value="only">Only duplicates</option>
              </select>
              {role && role.isAdmin && dn > 0 && (
                <span style={{ marginLeft: 6 }}>
                  <Btn onClick={openDupFix} title="Archive and remove the imported duplicates in this filter">Remove…</Btn></span>
              )}
            </div>
          );
        })()}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {["transactions", "balances"].map((t) => (
            <Btn key={t} variant={tab === t ? "primary" : undefined} onClick={() => setTab(t)}>
              {t === "transactions" ? "Transactions" : "Balances"}
            </Btn>
          ))}
        </div>
      </div>
    </Card>

    <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
      <MultiPick label="Status" selected={statuses} onChange={setStatuses} height={110}
                 options={opts(f.statuses.map((s) => ({ value: s, label: s })), statuses)} />
      <MultiPick label="Company Name" selected={companies} onChange={setCompanies}
                 options={opts(f.companies.map((c) => ({ value: c.accountId, label: c.companyname })), companies)} />
      <MultiPick label="Bank Name" selected={banks} onChange={setBanks}
                 options={opts(f.banks.map((b) => ({ value: b, label: b })), banks)} />
      <MultiPick label="Last 4 Acct" selected={last4} onChange={setLast4}
                 options={opts(f.last4.map((l) => ({ value: l, label: l })), last4)} />
      <MultiPick label="Pending / Posted" selected={txnStatus} onChange={setTxnStatus} height={70}
                 options={opts((f.txnstatus || []).map((s) => ({ value: s, label: s })), txnStatus)} />
    </div>

    {dupFix && <Modal title="Remove likely duplicates" onClose={() => !(dupFix && dupFix.busy) && setDupFix(null)} wide>
      {dupFix.loading ? <Spinner /> : dupFix.done ? <>
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          Removed <b>{num(dupFix.done.removed)}</b> imported duplicate{dupFix.done.removed === 1 ? "" : "s"}. Each is archived in
          customerBankTransactionDedupeArchive with the row it duplicated and your name, so it can be put back.
        </div>
        <Btn variant="primary" onClick={() => setDupFix(null)}>Close</Btn>
      </> : <>
        {dupFix.err && <div style={{ color: "#993C1D", fontSize: 13, marginBottom: 10 }}>{dupFix.err}</div>}
        {dupFix.data && <>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            In {companies.size ? `the ${companies.size} borrower${companies.size === 1 ? "" : "s"} selected` : "every borrower"},{" "}
            {from ? fmtDate(from) : "the start"} to {to ? fmtDate(to) : "today"}:{" "}
            <b>{num(dupFix.data.flagged)}</b> flagged, <b>{num(dupFix.data.removable)}</b> can be removed
            {dupFix.data.removable ? ` (${fmt(Math.abs(dupFix.data.amount || 0))})` : ""}.
          </div>
          <div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 10 }}>
            Only <b>imported</b>, posted rows are removed; the row each one duplicates stays. Left alone:
            {" "}{num(dupFix.data.kept_plaid)} from live Plaid (the next sync would add them back),
            {" "}{num(dupFix.data.kept_migrated)} migrated ("Bring transactions across" would add them back) and
            {" "}{num(dupFix.data.kept_pending)} pending (replaced when they post). For those, fix the mapping on Bank Acct Mapping.
            Every removed row is archived first, so it can be put back.
          </div>
          {(dupFix.data.rows || []).length > 0 && <div style={{ maxHeight: 320, overflow: "auto", marginBottom: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr><Th>Date</Th><Th>Borrower</Th><Th>Last 4</Th><Th>Amount</Th><Th>Removed</Th><Th>Kept ({"feed"})</Th></tr></thead>
              <tbody>{dupFix.data.rows.map((r) => (
                <tr key={r.removed_id}>
                  <Td>{fmtDate(r.txnDate)}</Td><Td>{r.companyname}</Td><Td>{r.last4 || "—"}</Td>
                  <Td style={{ textAlign: "right" }}><Money v={r.signedamount} /></Td>
                  <Td style={{ whiteSpace: "normal", maxWidth: 260 }}>{r.removed_description}</Td>
                  <Td style={{ whiteSpace: "normal", maxWidth: 260 }}>{r.kept_description} <span style={{ color: INK_MUTED }}>({r.kept_feed})</span></Td>
                </tr>))}</tbody>
            </table>
            {dupFix.data.removable > (dupFix.data.rows || []).length &&
              <div style={{ fontSize: 11, color: INK_MUTED, marginTop: 4 }}>First {dupFix.data.rows.length} of {num(dupFix.data.removable)} shown.</div>}
          </div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn onClick={() => setDupFix(null)} disabled={dupFix.busy}>Cancel</Btn>
            {dupFix.data.removable > 0 &&
              <Btn variant="danger" disabled={dupFix.busy} onClick={commitDupFix}>
                {dupFix.busy ? "Removing…" : `Archive and remove ${num(dupFix.data.removable)}`}</Btn>}
          </div>
        </>}
      </>}
    </Modal>}

    {err && <Card style={{ padding: "10px 14px", marginBottom: 12, background: "#FAECE7",
                           borderLeft: "3px solid #993C1D" }}>
      <span style={{ fontSize: 12, color: "#993C1D" }}>{err}</span>
    </Card>}

    {tab === "transactions" ? <>
      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <Stat label="Cash in"  value={fmt(totals.cash_in || 0)} />
        <Stat label="Cash out" value={fmt(totals.cash_out || 0)} />
        <Stat label="Net"      value={fmt(totals.net || 0)} />
        <Stat label="Transactions" value={num(totals.txns)}
              note={`${num(totals.accounts)} borrower${totals.accounts === 1 ? "" : "s"}`} />
        {/* Only when there is pending money to report. A tile reading "0" every
            day trains people to stop looking at it, which is the opposite of
            what it is for. */}
        {Number(totals.pending_txns) > 0 && (
          <Stat label="Pending" value={fmt(totals.pending_amount || 0)}
                note={`${num(totals.pending_txns)} not yet settled`} />
        )}
        <Stat label="Range" value={totals.first_txn ? fmtDate(totals.first_txn) : "—"}
              note={totals.last_txn ? `through ${fmtDate(totals.last_txn)}` : null} />
      </div>

      <Card style={{ padding: 14, marginBottom: 12, ...chartCardStyle(chartT) }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2, color: chartT.ink }}>Net cash by date</div>
          <ChartThemeToggle />
        </div>
        <div style={{ fontSize: 11, color: chartT.muted, marginBottom: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span>Money in positive. Hover the 60 largest days for the breakdown.
            <strong> Drag across the chart</strong> to filter to those dates, or click a day for just that day.</span>
          {zoomStack.length > 0 && (
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <Btn onClick={zoomBack}>← Back</Btn>
              <Btn onClick={() => { const first = zoomStack[0]; setFrom(first.from); setTo(first.to); setZoomStack([]); }}>Reset</Btn>
            </span>
          )}
        </div>
        <NetByDay series={(data && data.series) || []} onSelect={zoomTo} />
      </Card>

      <Card style={{ overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center",
                      marginBottom: 10, flexWrap: "wrap" }}>
          <SearchBar value={q} onChange={setQ} placeholder="Company, bank, last four, description…" />
          {/* The true total, the page, and the range in force — so a filter that
              changes the count but not the first page still reads as working. */}
          <span style={{ fontSize: 12, color: INK_MUTED }}>
            {`${num(grid.total)} transaction${grid.total === 1 ? "" : "s"}`}
            {qServer && ` matching "${qServer}"`}
            {grid.total > GRID_SIZE && ` · page ${num(gridPage)} of ${num(gridPages)}`}
            {(from || to) && ` · ${from ? fmtDate(from) : "start"} to ${to ? fmtDate(to) : "today"}`}
            {gridLoading && <span style={{ color: B.cyanDark }}> · loading…</span>}
          </span>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead><tr>
            <H label="Company" k="companyname" /><H label="Bank" k="bankname" />
            <H label="Last 4" k="last4" /><H label="Date" k="txnDate" />
            <H label="Amount" k="signedamount" numeric /><H label="Description" k="name" />
            {/* Two columns where there used to be one. "Status" carried the
                borrower's standing and the transaction's pending flag glued
                together with a middot, which read as a single fact and made
                neither filterable. They are separate things about separate
                objects and now they look like it. */}
            <H label="Pending / Posted" k="txnstate" /><H label="Account Status" k="accountstatus" />
          </tr></thead>
          <tbody>
            {shownRows.map((r) => (
              <tr key={r.id}>
                <Td>{r.companyname || "—"}</Td>
                <Td>{r.bankname || <span style={{ color: "#8A6100" }}>Unmapped</span>}</Td>
                <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                  {r.last4 || "—"}</span></Td>
                <Td>{fmtDate(r.txnDate)}</Td>
                <Td style={{ textAlign: "right" }}><Money v={r.signedamount} /></Td>
                <Td style={{ whiteSpace: "normal", maxWidth: 460 }}>
                  {r.isdup ? <span title={`Same account, date and amount as transaction ${r.dupofid}${r.feed ? " · this one came from " + r.feed : ""}. Not counted unless duplicates are shown.`}
                                   style={{ fontSize: 10.5, fontWeight: 600, padding: "1px 7px", borderRadius: 10, marginRight: 6,
                                            background: "#FDF0D5", color: "#7A5200", whiteSpace: "nowrap" }}>Likely duplicate</span> : null}
                  {r.name || "—"}
                </Td>
                <Td><TxnState pending={r.pending} /></Td>
                <Td><span style={{ fontSize: 12, color: INK_MUTED }}>
                  {r.accountstatus || "—"}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={gridPage} totalPages={gridPages} total={grid.total} pageSize={GRID_SIZE} onPage={setGridPage} />
      </Card>
    </> : (
      <Card style={{ overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center",
                      marginBottom: 10, flexWrap: "wrap" }}>
          <SearchBar value={q} onChange={setQ} placeholder="Company, bank, last four…" />
          <span style={{ fontSize: 12, color: INK_MUTED }}>
            Latest reading per account{from || to !== today ? " within the date range" : ""} ·{" "}
            {num(shownBal.length)} account{shownBal.length === 1 ? "" : "s"}
          </span>
          {(bal === null || balLoading) && <span style={{ fontSize: 12, color: B.cyanDark }}>Loading balances…</span>}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead><tr>
            <Th>Company</Th><Th>Bank</Th><Th>Last 4</Th><Th>As of</Th>
            <Th>Current</Th><Th>Available</Th><Th>Status</Th>
          </tr></thead>
          <tbody>
            {shownBal.map((b) => (
              <tr key={b.accountId + "|" + b.bankname + "|" + b.last4 + "|" + b.asofDate}>
                <Td>{b.companyname || "—"}</Td>
                <Td>{b.bankname || <span style={{ color: "#8A6100" }}>Unmapped</span>}</Td>
                <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                  {b.last4 || "—"}</span></Td>
                <Td>{fmtDate(b.asofDate)}</Td>
                <Td style={{ textAlign: "right" }}><Money v={b.currentBalance} /></Td>
                <Td style={{ textAlign: "right" }}><Money v={b.availableBalance} /></Td>
                <Td><span style={{ fontSize: 12, color: INK_MUTED }}>
                  {b.accountstatus || "—"}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    )}
  </>;
};
